/**
 * Checks if the current device is mobile or tablet.
 * @returns True if the device is mobile or tablet, otherwise false.
 */
export const mobileAndTabletCheck = (): boolean => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  const isMobileOrTablet = /android|bb\d+|meego|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|iphone|ipod|iris|kindle|lge |maemo|midp|mmp|mobile|netfront|opera m|palm|phone|plucker|pocket|psp|symbian|treo|vodafone|wap|windows ce|xda|xiino/i.test(userAgent);
  const isSmallScreen = window.innerWidth < 768;

  return isMobileOrTablet || isSmallScreen;
};

/**
 * Tests the user agent and platform against a given regular expression.
 * @param regex - The regular expression to test against.
 * @returns True if the regex matches, otherwise false.
 */
const testRegexForDevice = (regex: RegExp): boolean => {
  const matchesUserAgent = regex.test(navigator.userAgent);
  const matchesPlatform = regex.test(navigator.platform);
  const matchesUserAgentData = navigator.userAgentData ? regex.test(navigator.userAgentData.platform) : false;

  return matchesUserAgent || matchesPlatform || matchesUserAgentData;
};

/**
 * Checks if the device is an Android device.
 * @returns True if the device is Android, otherwise false.
 */
const isAndroidDevice = (): boolean => testRegexForDevice(/Android/i);

/**
 * Checks if the device is an iOS device.
 * @returns True if the device is iOS, otherwise false.
 */
const isIOSDevice = (): boolean => testRegexForDevice(/iPad|iPod|iPhone/i);

/**
 * Checks if the device is a macOS device.
 * @returns True if the device is macOS, otherwise false.
 */
const isMacOSDevice = (): boolean => testRegexForDevice(/Macintosh|MacIntel|MacPPC|Mac68K/i);

/**
 * Checks if the device is a Windows OS device.
 * @returns True if the device is Windows, otherwise false.
 */
const isWindowsOSDevice = (): boolean => testRegexForDevice(/Windows/i);

/**
 * Checks if the device is a Linux OS device.
 * @returns True if the device is Linux, otherwise false.
 */
const isLinuxOSDevice = (): boolean => testRegexForDevice(/Linux/i);

/**
 * Gets the type of device.
 * @returns 'Mobile Web' if the device is mobile or tablet, otherwise 'Web Desktop'.
 */
export const getDevice = (): string => (mobileAndTabletCheck() ? 'Mobile Web' : 'Web Desktop');

/**
 * Gets the operating system of the device.
 * @returns The name of the OS or undefined if not recognized.
 */
export const getDeviceOS = (): string | undefined => {
  if (isAndroidDevice()) return 'Android';
  if (isIOSDevice()) return 'iOS';
  if (isMacOSDevice()) return 'macOS';
  if (isWindowsOSDevice()) return 'Windows';
  if (isLinuxOSDevice()) return 'Linux';
  return undefined;
};
